/**
 * The order-form vocabulary: what the engine actually accepts (doc 02 §2.2–2.3),
 * so the ticket never offers a combination the state machine will refuse.
 *
 * - Order types are `MARKET`, `LIMIT`, `STOP`, `STOP_LIMIT`. `STOP` needs a stop
 *   price, `LIMIT`/`STOP_LIMIT` need a limit price, `MARKET` needs neither.
 * - `MARKET` accepts **only** `IOC` or `FOK` (doc 02 §2.3); offering `GTC` on a
 *   market order would earn `E_TIF_NOT_ALLOWED` every time.
 * - `SPECIFIED` is deliberately not offered: it expires at an `expires_at` the
 *   ticket does not collect, and a time with no time is a rejected order.
 *
 * A symbol can disable a type (`allowed_order_types`), so the ticket intersects
 * this list with the server's own.
 */

import type { OrderType } from '@weke/domain';

export interface OrderTypeOption {
  readonly id: OrderType;
  readonly label: string;
  /** A short hint under the select, for the trader. */
  readonly hint: string;
}

export const ORDER_TYPES_FOR_UI: readonly OrderTypeOption[] = [
  { id: 'MARKET', label: 'Market', hint: 'Fill now at the best available price.' },
  { id: 'LIMIT', label: 'Limit', hint: 'Fill at the limit price or better.' },
  { id: 'STOP', label: 'Stop', hint: 'Trigger a market order when the stop price trades.' },
  {
    id: 'STOP_LIMIT',
    label: 'Stop limit',
    hint: 'Trigger, then rest as a limit order at the limit price.',
  },
];

export interface TifOption {
  readonly id: string;
  readonly label: string;
}

const MARKET_TIFS: readonly TifOption[] = [
  { id: 'IOC', label: 'Immediate (IOC)' },
  { id: 'FOK', label: 'Fill or kill' },
];

const PENDING_TIFS: readonly TifOption[] = [
  { id: 'GTC', label: 'Good till cancelled' },
  { id: 'DAY', label: 'Day' },
  { id: 'IOC', label: 'Immediate (IOC)' },
  { id: 'FOK', label: 'Fill or kill' },
];

/** Valid TIFs for a type. A market order cannot rest, so it cannot be `GTC`. */
export function tifsFor(type: string): readonly TifOption[] {
  return type === 'MARKET' ? MARKET_TIFS : PENDING_TIFS;
}

export interface TypeNeeds {
  readonly limit: boolean;
  readonly stop: boolean;
}

export function orderTypeNeeds(type: string): TypeNeeds {
  switch (type) {
    case 'LIMIT':
      return { limit: true, stop: false };
    case 'STOP':
      return { limit: false, stop: true };
    case 'STOP_LIMIT':
      return { limit: true, stop: true };
    default:
      return { limit: false, stop: false };
  }
}

/**
 * The types this symbol accepts, in UI order.
 *
 * `pendingOrders: false` is a **licence** restriction, not a symbol one: a tenant
 * without `core.pending_orders` may open at market and close, and nothing else
 * (docs/05 §5 — the server refuses the same types, so this is only the rendering
 * half of the same rule).
 */
export function typesForSymbol(
  allowed: readonly OrderType[],
  options: { readonly pendingOrders?: boolean } = {},
): readonly OrderTypeOption[] {
  const pending = options.pendingOrders !== false;
  const permitted = ORDER_TYPES_FOR_UI.filter(
    (entry) => allowed.includes(entry.id) && (pending || entry.id === 'MARKET'),
  );
  return permitted.length === 0 ? [ORDER_TYPES_FOR_UI[0] as OrderTypeOption] : permitted;
}
